import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    HiOutlineDocumentText, 
    HiOutlineCalendar, 
    HiOutlineSpeakerphone,
    HiOutlinePhotograph,
    HiOutlineEye,
    HiOutlineArrowRight,
    HiOutlineChartBar
} from 'react-icons/hi';
import { eventsService, announcementsService, blogService, bannersService } from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
    const { user, refreshUser } = useAuth();
    const [stats, setStats] = useState({
        events: 0,
        announcements: 0,
        blog: 0,
        banners: 0
    });
    const [loading, setLoading] = useState(true);
    const [cardsKey, setCardsKey] = useState(0); // Forzar re-render de tarjetas

    // Refrescar permisos cuando se monta el dashboard
    useEffect(() => {
        refreshUser();
    }, []);

    // Actualizar tarjetas cuando cambian los permisos
    useEffect(() => {
        setCardsKey(prev => prev + 1);
        console.log('📊 Dashboard actualizado. Usuario:', user?.email, 'Permisos:', user?.permissions);
    }, [user?.permissions]);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const [eventsRes, announcementsRes, blogRes, bannersRes] = await Promise.all([
                eventsService.getAll(),
                announcementsService.getAll(),
                blogService.getAll(),
                bannersService.getAll()
            ]);
            
            setStats({
                events: eventsRes.data.length,
                announcements: announcementsRes.data.length,
                blog: blogRes.data.posts?.length || 0,
                banners: bannersRes.data.length
            });
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setLoading(false);
        }
    };

    // Verificar permiso basándose en permisos específicos del usuario
    const hasPermission = (module) => {
        if (!user) return false;
        
        // Verificar permisos específicos del usuario (todos, incluyendo admins)
        const userPermissions = Array.isArray(user?.permissions) ? user.permissions : [];
        const hasIt = userPermissions.includes(module);
        
        console.log(`Dashboard: verificando módulo "${module}": ${hasIt}. Permisos actuales:`, userPermissions);
        
        return hasIt;
    };

    const allStatCards = [
        {
            title: 'Eventos',
            value: stats.events,
            icon: <HiOutlineCalendar />,
            link: '/events',
            color: 'white',
            bgColor: 'linear-gradient(135deg, #123558 0%, #1a4d7a 100%)',
            module: 'events'
        },
        {
            title: 'Convocatorias',
            value: stats.announcements,
            icon: <HiOutlineSpeakerphone />,
            link: '/announcements',
            color: 'white',
            bgColor: 'linear-gradient(135deg, #4a90e2 0%, #6eb5ff 100%)',
            module: 'announcements'
        },
        {
            title: 'Artículos de Blog',
            value: stats.blog,
            icon: <HiOutlineDocumentText />,
            link: '/blog',
            color: 'white',
            bgColor: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            module: 'blog'
        },
        {
            title: 'Banners',
            value: stats.banners,
            icon: <HiOutlinePhotograph />,
            link: '/banners',
            color: 'white',
            bgColor: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
            module: 'banners'
        }
    ];

    const statCards = allStatCards.filter(card => hasPermission(card.module));

    const allQuickActions = [
        { title: 'Crear Evento', link: '/events', icon: <HiOutlineCalendar />, module: 'events' },
        { title: 'Nueva Convocatoria', link: '/announcements', icon: <HiOutlineSpeakerphone />, module: 'announcements' },
        { title: 'Nuevo Artículo', link: '/blog', icon: <HiOutlineDocumentText />, module: 'blog' },
        { title: 'Agregar Banner', link: '/banners', icon: <HiOutlinePhotograph />, module: 'banners' }
    ];

    const quickActions = allQuickActions.filter(action => hasPermission(action.module));

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando dashboard...</p>
            </div>
        );
    }

    return (
        <div className="content-manage">
            <div className="dashboard-header">
                <div>
                    <h1>Dashboard</h1>
                    <p>Bienvenido al sistema gestor de contenido de Fenine</p>
                </div>
            </div>

            <div className="stats-grid">
                {statCards.map((stat, index) => (
                    <Link key={index} to={stat.link} className="stat-card card">
                        <div className="stat-icon" style={{ background: stat.bgColor, color: stat.color }}>
                            {stat.icon}
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{stat.value}</div>
                            <div className="stat-title">{stat.title}</div>
                        </div>
                        <div className="stat-arrow">
                            <HiOutlineArrowRight />
                        </div>
                    </Link>
                ))}
            </div>

            <div className="grid grid-cols-2" style={{ marginTop: 'var(--space-xl)' }}>
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">Accesos Rápidos</h3>
                    </div>
                    <div className="card-body">
                        <div className="quick-actions">
                            {quickActions.map((action, index) => (
                                <Link key={index} to={action.link} className="quick-action-item">
                                    <div className="quick-action-icon">
                                        {action.icon}
                                    </div>
                                    <span>{action.title}</span>
                                    <HiOutlineArrowRight className="arrow" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">Estadísticas</h3>
                    </div>
                    <div className="card-body">
                        <div className="stats-summary">
                            <div className="summary-item">
                                <HiOutlineChartBar />
                                <div>
                                    <div className="summary-label">Total de Contenidos</div>
                                    <div className="summary-value">{stats.events + stats.announcements + stats.blog + stats.banners}</div>
                                </div>
                            </div>
                            <div className="summary-item">
                                <HiOutlineEye />
                                <div>
                                    <div className="summary-label">Contenidos Activos</div>
                                    <div className="summary-value">{stats.events + stats.announcements}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
